import {useRef, useState, type KeyboardEvent} from 'react';
import {ChevronRight, Search} from 'lucide-react';
import {Badge, Input} from '@kroll-resolver-crisp/ui-components-react';
import {type ClassificationTreeNode, type PolicyViolationQueueSummary} from '@/api/moderationQueue';
import {classificationPath, nodeKey, searchTree, type QueueFilters} from '@/lib/queueFilters';
import {hasSelection, type QueueGroupSelection} from '@/lib/queueGroups';
import {cn} from '@/lib/utils';

type Selection = Pick<QueueFilters, 'vertical' | 'classification' | 'scope'>;

type Props = {
  readonly filters: QueueFilters;
  // Per vertical the analyst may review, already narrowed by language and kind.
  readonly summaries: readonly PolicyViolationQueueSummary[];
  readonly onSelect: (selection: Selection) => void;
  // A queue group being built: rows become checkboxes and picking one toggles it in the group.
  readonly editing?:
    | {
        readonly selections: readonly QueueGroupSelection[];
        readonly onToggle: (selection: QueueGroupSelection) => void;
      }
    | undefined;
};

type Row = {
  readonly id: string;
  readonly level: number;
  readonly label: string;
  readonly count: number;
  readonly selected: boolean;
  readonly expandable: boolean;
  readonly expanded: boolean;
  readonly parentId: string | null;
  readonly italic: boolean;
  // What the row adds to a queue group; null when it can't be one (All verticals, the two buckets, a node
  // with no concept).
  readonly member: QueueGroupSelection | null;
  readonly select: () => void;
  readonly toggle: () => void;
};

type Vertical = {
  readonly name: string;
  readonly available: number;
  readonly tree: readonly ClassificationTreeNode[];
};

const ALL_ID = 'all';
const INDENT_REM = 0.9;
const verticalId = (name: string) => `/${name}`;

// The ids of the vertical and of the ancestors of the current classification, so a selection opens revealed.
function revealedIds(summaries: readonly PolicyViolationQueueSummary[], filters: QueueFilters) {
  const ids = new Set<string>();
  if (!filters.vertical) {
    return ids;
  }

  let prefix = verticalId(filters.vertical);
  ids.add(prefix);
  const tree = summaries.find((s) => s.policy_violation === filters.vertical)?.tree ?? [];
  const path = filters.classification ? classificationPath(tree, filters.classification) : null;
  for (const node of path?.slice(0, -1) ?? []) {
    prefix = `${prefix}/${node.name}`;
    ids.add(prefix);
  }

  return ids;
}

// One navigation tree: "All verticals", then each vertical with its taxonomy beneath it. Picking a vertical
// opens its taxonomy and closes the one it replaces unless the analyst opened that one by hand. A search
// spans every vertical and keeps the ancestors of a match visible and expanded.
export function QueueNavTree({filters, summaries, onSelect, editing}: Props) {
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState(() => revealedIds(summaries, filters));
  const [manual, setManual] = useState<ReadonlySet<string>>(() => new Set());
  const [focusId, setFocusId] = useState<string | null>(null);
  const items = useRef(new Map<string, HTMLElement>());
  const searching = query.trim() !== '';
  const needle = query.trim().toLowerCase();

  const verticals: Vertical[] = summaries.map((s) => ({
    name: s.policy_violation,
    available: s.available,
    tree: s.tree
  }));
  if (filters.vertical && !verticals.some((v) => v.name === filters.vertical)) {
    verticals.push({name: filters.vertical, available: 0, tree: []});
  }

  const toggleId = (id: string) => {
    setExpanded((previous) => {
      const next = new Set(previous);
      if (!next.delete(id)) {
        next.add(id);
      }

      return next;
    });
  };

  const toggleVertical = (name: string) => {
    const id = verticalId(name);
    setManual((previous) => {
      const next = new Set(previous);
      if (expanded.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }

      return next;
    });
    toggleId(id);
  };

  const selectVertical = (name: string) => {
    setExpanded((previous) => {
      const next = new Set(previous);
      const before = filters.vertical ? verticalId(filters.vertical) : null;
      if (before && filters.vertical !== name && !manual.has(before)) {
        next.delete(before);
      }

      next.add(verticalId(name));
      return next;
    });
    onSelect({vertical: name, classification: null, scope: null});
  };

  const rows: Row[] = [];
  if (!searching) {
    rows.push({
      id: ALL_ID,
      level: 1,
      label: 'All verticals',
      count: verticals.reduce((sum, v) => sum + v.available, 0),
      selected: filters.vertical === null && filters.group === null,
      expandable: false,
      expanded: false,
      parentId: null,
      italic: false,
      member: null,
      select() {
        onSelect({vertical: null, classification: null, scope: null});
      },
      toggle: () => undefined
    });
  }

  const pushNodes = (
    nodes: readonly ClassificationTreeNode[],
    parentId: string,
    level: number,
    {vertical, forcedOpen}: {vertical: string; forcedOpen: ReadonlySet<string>}
  ) => {
    for (const node of nodes) {
      const id = `${parentId}/${node.name}`;
      const key = nodeKey(node, level === 2);
      const inVertical = filters.vertical === vertical && filters.classification === key;
      const isOpen =
        node.children.length > 0 && (searching ? forcedOpen.has(id) : expanded.has(id));
      rows.push({
        id,
        level,
        label: node.name,
        count: node.available,
        selected: inVertical && filters.scope === null,
        expandable: node.children.length > 0,
        expanded: isOpen,
        parentId,
        italic: false,
        member: node.concept_id ? {vertical, classification: key, scope: null} : null,
        select() {
          onSelect({vertical, classification: key, scope: null});
        },
        toggle() {
          toggleId(id);
        }
      });
      if (!isOpen) {
        continue;
      }

      if (node.own > 0) {
        rows.push({
          id: `${id}#own`,
          level: level + 1,
          label: '(this level only)',
          count: node.own,
          selected: inVertical && filters.scope === 'own',
          expandable: false,
          expanded: false,
          parentId: id,
          italic: true,
          member: node.concept_id ? {vertical, classification: key, scope: 'own'} : null,
          select() {
            onSelect({vertical, classification: key, scope: 'own'});
          },
          toggle: () => undefined
        });
      }

      pushNodes(node.children, id, level + 1, {vertical, forcedOpen});
    }
  };

  for (const vertical of verticals) {
    const id = verticalId(vertical.name);
    const {nodes, open} = searchTree(vertical.tree, query);
    const nameMatches = vertical.name.toLowerCase().includes(needle);
    if (searching && !nameMatches && nodes.length === 0) {
      continue;
    }

    const expandable = vertical.tree.length > 0;
    const isOpen = expandable && (searching ? nodes.length > 0 : expanded.has(id));
    rows.push({
      id,
      level: 1,
      label: vertical.name,
      count: vertical.available,
      selected: filters.vertical === vertical.name && filters.classification === null,
      expandable,
      expanded: isOpen,
      parentId: null,
      italic: false,
      member: {vertical: vertical.name, classification: null, scope: null},
      select() {
        selectVertical(vertical.name);
      },
      toggle() {
        toggleVertical(vertical.name);
      }
    });
    if (isOpen) {
      pushNodes(nodes, id, 2, {
        vertical: vertical.name,
        forcedOpen: new Set([...open].map((path) => `${id}${path}`))
      });
    }
  }

  const checked = (row: Row) =>
    editing !== undefined && row.member !== null && hasSelection(editing.selections, row.member);
  // While a group is being built, picking a row toggles it in the group instead of filtering.
  const pick = (row: Row) => {
    if (!editing) {
      row.select();
    } else if (row.member) {
      editing.onToggle(row.member);
    }
  };

  const tabStop =
    rows.find((row) => row.id === focusId)?.id ??
    rows.find((row) => row.selected)?.id ??
    rows[0]?.id;

  const focusRow = (id: string | undefined) => {
    if (id !== undefined) {
      setFocusId(id);
      items.current.get(id)?.focus();
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    const id = (event.target as HTMLElement).closest<HTMLElement>('[role="treeitem"]')?.dataset
      .rowId;
    const index = rows.findIndex((row) => row.id === id);
    const row = rows[index];
    if (!row) {
      return;
    }

    const handled = () => {
      event.preventDefault();
    };

    switch (event.key) {
      case 'ArrowDown': {
        handled();
        focusRow(rows[index + 1]?.id);
        break;
      }

      case 'ArrowUp': {
        handled();
        focusRow(rows[index - 1]?.id);
        break;
      }

      case 'Home': {
        handled();
        focusRow(rows[0]?.id);
        break;
      }

      case 'End': {
        handled();
        focusRow(rows.at(-1)?.id);
        break;
      }

      case 'ArrowRight': {
        handled();
        if (row.expandable && !row.expanded) {
          row.toggle();
        } else if (row.expandable) {
          focusRow(rows[index + 1]?.id);
        }

        break;
      }

      case 'ArrowLeft': {
        handled();
        if (row.expandable && row.expanded) {
          row.toggle();
        } else {
          focusRow(row.parentId ?? undefined);
        }

        break;
      }

      case 'Enter':
      case ' ': {
        handled();
        pick(row);
        break;
      }

      default: {
        break;
      }
    }
  };

  return (
    <div className="flex min-h-0 flex-col gap-2">
      <div className="relative">
        <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-2 size-3.5 -translate-y-1/2" />
        <Input
          type="search"
          aria-label="Search verticals and classifications"
          placeholder="Search verticals and classifications"
          className="pl-7"
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
          }}
        />
      </div>
      {rows.length > 0 ? (
        <div
          role="tree"
          aria-label="Verticals"
          className="flex min-h-0 flex-col overflow-auto"
          onKeyDown={onKeyDown}>
          {rows.map((row) => (
            // Keys are handled once for the whole tree by the roving-tabindex onKeyDown above.
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events
            <div
              key={row.id}
              ref={(element) => {
                if (element) {
                  items.current.set(row.id, element);
                } else {
                  items.current.delete(row.id);
                }
              }}
              role="treeitem"
              data-row-id={row.id}
              aria-level={row.level}
              aria-selected={editing ? undefined : row.selected}
              aria-checked={editing && row.member ? checked(row) : undefined}
              aria-expanded={row.expandable ? row.expanded : undefined}
              tabIndex={row.id === tabStop ? 0 : -1}
              className={cn(
                'hover:bg-muted focus-visible:ring-ring flex cursor-pointer items-start gap-1 py-1 pr-2 text-xs outline-none focus-visible:ring-1',
                !editing && row.selected && 'bg-muted font-medium',
                row.italic && (editing || !row.selected) && 'text-muted-foreground italic',
                editing && !row.member && 'cursor-default opacity-60'
              )}
              style={{paddingLeft: `${(row.level - 1) * INDENT_REM + 0.25}rem`}}
              onFocus={() => {
                setFocusId(row.id);
              }}
              onClick={() => {
                pick(row);
              }}>
              {row.expandable ? (
                <button
                  type="button"
                  tabIndex={-1}
                  aria-label={`${row.expanded ? 'Collapse' : 'Expand'} ${row.label}`}
                  className="text-muted-foreground hover:text-foreground flex size-4 shrink-0 items-center justify-center"
                  onClick={(event) => {
                    event.stopPropagation();
                    row.toggle();
                  }}>
                  <ChevronRight
                    className={cn('size-3.5 transition-transform', row.expanded && 'rotate-90')}
                  />
                </button>
              ) : (
                <span aria-hidden className="size-4 shrink-0" />
              )}
              {editing && row.member ? (
                <input
                  aria-hidden
                  readOnly
                  type="checkbox"
                  tabIndex={-1}
                  checked={checked(row)}
                  className="pointer-events-none mt-0.5 size-3 shrink-0"
                />
              ) : null}
              <span className="min-w-0 flex-1 break-words" title={row.label}>
                {row.label}
              </span>
              <Badge
                variant={!editing && row.selected ? 'default' : 'secondary'}
                className="shrink-0 tabular-nums">
                {row.count}
              </Badge>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-muted-foreground px-1 text-xs">No vertical or classification matches.</p>
      )}
    </div>
  );
}
